<template>
  <div class="users-tab" v-if="!isMembersPending && !isOrganisationPending">
    <div class="cards-inline">
      <div class="card users-stats">
        <div class="stats-inline">
          <div class="stat">
            <div class="top">
              <div class="icon-container">
                <i class="bi bi-person-check" />
              </div>
              <span>
                {{ members.length }}
              </span>
            </div>
            {{ $t("app.company.members.seatUsed", { count: members.length }) }}
          </div>
          <div class="stat">
            <div class="top">
              <div class="icon-container">
                <i class="bi bi-person-add" />
              </div>
              <span :class="{ big: !organisation.maximum_members_count }">
                {{ !organisation.maximum_members_count ? "∞" : organisation.maximum_members_count - members.length }}
              </span>
            </div>
            {{ $t("app.company.members.seatLeft", { count: !organisation.maximum_members_count || organisation.maximum_members_count - members.length !== 1 }) }}
          </div>
        </div>
        <Toast v-if="organisation.maximum_members_count" :text="$t('app.company.members.needMoreUsers')" color="yellow" icon="bi-info-circle" :closable="false" @click="$router.push({ name: 'contact' })" />
      </div>
      <div class="card add-user">
        <h4>{{ $t("app.company.members.addUserTitle") }}</h4>
        <VForm
          :button="{
            label: $t('app.company.members.addUserBtn'),
            class: 'primary full-size',
            hotDisabled: true,
          }"
          :externalRule="state.role !== null"
          :inline="false"
          :displayErrors="false"
          :callback="addNewMember"
          :clean="true"
        >
          <template #form-body>
            <div class="inline">
              <VField :label="$t('app.company.members.addUserEmail')" name="add-user-email" :placeholder="$t('app.company.members.addUserEmailPlaceholder')" type="email" v-model="state.mail" rules="required|email|max:50" :disabled="!hasSlotAvailable" :displayError="true" />
              <div class="input-wrapper">
                <label for="add-user-role"
                  >{{ $t("app.company.members.userRoleLabel") }}
                  <VTooltip placement="auto" :triggers="['hover']" theme="large">
                    &nbsp;<i class="bi bi-info-circle" />
                    <template #popper>
                      <i18n-t tag="p" keypath="app.company.members.adminRoleDescription">
                        <template #role>
                          <span class="primary-color bold">
                            {{ $t("app.company.members.Administrator") }}
                          </span>
                        </template>
                      </i18n-t>
                      <ul>
                        <li>{{ $t("app.company.members.adminRoleAccess") }}</li>
                        <li>{{ $t("app.company.members.adminRoleCredit") }}</li>
                      </ul>
                      <br />
                      <i18n-t tag="p" keypath="app.company.members.userRoleDescription">
                        <template #role>
                          <span class="primary-color bold">
                            {{ $t("app.company.members.Member") }}
                          </span>
                        </template>
                      </i18n-t>
                      <ul>
                        <li>{{ $t("app.company.members.userRoleAccess") }}</li>
                        <li>{{ $t("app.company.members.userRoleCredit") }}</li>
                      </ul>
                    </template>
                  </VTooltip>
                </label>
                <v-select
                  id="add-user-role"
                  name="add-user-role"
                  label="name"
                  v-model="state.role"
                  :placeholder="$t('app.company.members.selectRolePlaceholder')"
                  :searchable="false"
                  :reduce="(role: AppRole) => role.id"
                  :clearable="false"
                  :options="filteredOrganisationRoles"
                  :disabled="!hasSlotAvailable"
                >
                  <template #selected-option="option">
                    {{ $t(`app.company.members.${option.name}`) }}
                  </template>
                  <template #option="option">
                    {{ $t(`app.company.members.${option.name}`) }}
                  </template>
                </v-select>
              </div>
            </div>
            <div class="inline">
              <VField :label="$t('app.company.members.addUserFirstName')" name="add-user-first-name" placeholder="Evan" type="text" v-model="state.firstName" rules="required|max:50" :disabled="!hasSlotAvailable" :displayError="true" />
              <VField :label="$t('app.company.members.addUserLastName')" name="add-user-last-name" placeholder="Lee" type="text" v-model="state.lastName" rules="required|max:50" :disabled="!hasSlotAvailable" :displayError="true" />
            </div>
          </template>
        </VForm>
      </div>
    </div>
    <div class="card users-table">
      <h4>{{ $t("app.company.members.userList.title") }} <i style="font-size: 16px;" class="bi bi-info-circle" v-tooltip="$t('app.company.members.userList.subTitle')"></i></h4>
      <Table :data="[...members]" :fields="state.fields">
        <template #user="memberDetails: { data: MemberDetails }">
          <p v-if="!memberDetails?.data?.account" class="muted">{{ $t("app.company.members.userList.pending") }}</p>
          <div v-else class="container" style="gap: 8px">
            <img class="picture" :src="memberDetails?.data?.account?.personal_information?.profile_picture_id ? getImage(memberDetails?.data?.account?.personal_information?.profile_picture_id) : getRandomUserPicture(memberDetails?.data?.account?.personal_information?.first_name)" />
            <span>{{ memberDetails?.data?.account?.personal_information?.first_name }} {{ memberDetails?.data?.account?.personal_information?.last_name }}</span>
          </div>
        </template>
        <template #pending_email="memberDetails: { data: MemberDetails }">
          <div class="container" v-text="memberDetails?.data?.account?.personal_information?.email" />
        </template>
        <template #creation_time="memberDetails: { data: MemberDetails }">
          <div
            :style="{ width: 'fit-content' }"
            v-text="getDate(memberDetails?.data?.member?.created_at, 'LL')"
            v-tooltip="{
              content: getDate(memberDetails?.data?.member?.created_at, 'LLLL'),
              placement: 'top',
            }"
          />
        </template>
        <template #status="memberDetails: { data: MemberDetails }">
          <div class="container">
            <Tag :color="!memberDetails?.data?.account?.pending ? 'green' : 'grey-light'" :text="!memberDetails?.data?.account?.pending ? $t('app.company.members.userList.inviteAccepted') : $t('app.company.members.userList.invitePending')" />
          </div>
        </template>
        <template #role="memberDetails: { data: MemberDetails }">
          <div class="input-wrapper">
            <v-select
              id="role"
              name="role"
              label="name"
              :model-value="getRole(memberDetails?.data)"
              :searchable="false"
              :clearable="false"
              :disabled="user.id === memberDetails?.data.account.id || getRole(memberDetails?.data)?.role_backward_compatibility_id === 0"
              :options="filteredOrganisationRoles"
              @update:modelValue="updateRole($event, memberDetails?.data)"
            >
            <template #selected-option="option">
              {{ $t(`app.company.members.${option.name}`) }}
            </template>
            <template #option="option">
              {{ $t(`app.company.members.${option.name}`) }}
            </template>
          </v-select>
          </div>
        </template>
        <template #actions="memberDetails: { row: MemberDetails }">
          <Button :disabled="user.id === memberDetails?.row?.account?.id || getRole(memberDetails?.row)?.role_backward_compatibility_id === 0" :label="memberDetails?.row?.account ? $t('app.company.members.userList.revoke') : $t('app.company.members.userList.cancelAdd')" class="icon icon-left red outline" icon="bi-trash" @click="((state.showRevokeMember = true), (state.currentMember = memberDetails.row))" />
        </template>
      </Table>
    </div>
    <RevokeMember :isOpen="state.showRevokeMember" :member="state.currentMember" @confirm="revokeMember(state.currentMember)" @close="state.showRevokeMember = null" />
  </div>
</template>

<style lang="scss" scoped>
.inline {
  display: flex;
  gap: 8px;
}
</style>

<script setup lang="ts">
import { reactive, computed } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { getImage, getRandomUserPicture } from "@/composables/getImage.js";
import { getDate, dateIsBefore } from "@/composables/utils.js";
import { getError } from "@/composables/error.js";

import RevokeMember from "@/components/modals/app/RevokeMember.vue";
import { useUser } from "@/queries/user.query";

import { MemberDetails } from "@/types/member";
import { useInviteInOrganisation } from "@/queries/invitation.query";
import { useUpdateMemberRole, useMembersDetails, useDeleteMember } from "@/queries/members.query";
import { membersApi } from "@/api/members.api";
import { useQueries } from "@tanstack/vue-query";
import { useAllOrgnisationRoles, useOrganisation } from "@/queries/organisation.query";
import { AppRole, MemberRole } from "@/types/role";

const route = useRoute();
const toast = useToast();
const { t } = useI18n();

const appStore = useAppStore();

const { data: user, isPending: isUserPending } = useUser();
const { data: organisation, isPending: isOrganisationPending } = useOrganisation();
const { data: organisationRoles, isPending: isOrganisationRolesPending } = useAllOrgnisationRoles();
const { data: members, isPending: isMembersPending } = useMembersDetails();
const { mutate: updateMemberRole } =  useUpdateMemberRole();
const { mutate: deleteMember } = useDeleteMember();
const { mutate: inviteInOrganisation } = useInviteInOrganisation();

const queries = computed(() => {
  if (isMembersPending.value) return [];
  return members.value.map((memberDetails) =>  {
    return {
      queryKey: ['members', { organisationId: appStore.currentOrganisationId, memberId: memberDetails.member.id }, 'roles'],
      queryFn: async () => {
        const roles = await membersApi.getMemberRoles(memberDetails.member.id);
        return { memberId: memberDetails.member.id, role: roles?.[0] };
      }
    }
  });
});

const filteredOrganisationRoles = computed(() => {
  if (isOrganisationRolesPending.value) return [];
  return organisationRoles.value.filter((role) => {
    if (role.role_backward_compatibility_id === MemberRole.PARTCIPANT) return false;
    return true;
  })
});

const membersRoles = useQueries({
  queries: queries,
  combine: (results) => {
    return {
      data: results.map((result) => result.data),
      pending: results.some((result) => result.isPending),
    }
  },
});

const hasSlotAvailable = computed(() => {
  if (isOrganisationPending.value || isMembersPending.value) return false;
  if (!organisation.value.maximum_members_count) return true;

  return organisation.value.maximum_members_count - members.value.length > 0;
});

const state = reactive({
  mail: "",
  firstName: "",
  lastName: "",
  role: null,
  currentMember: null as MemberDetails,
  showRevokeMember: false,
  fields: [
    {
      id: "user",
      label: t("app.company.members.userList.table.columns.profile"),
      sort(a, b, dir) {
        if (!a && !b) return -1;
        if (!a && b) return 1;
        if (a && !b) return -1;

        if (dir === "ASC") {
          return `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
        }

        return `${b.first_name} ${b.last_name}`.localeCompare(`${a.first_name} ${a.last_name}`);
      },
    },
    {
      id: "pending_email",
      label: t("app.company.members.userList.table.columns.email"),
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "creation_time",
      label: t("app.company.members.userList.table.columns.date"),
      defaultSortDir: "DESC",
      sort(a, b, dir) {
        if (dir === "ASC") {
          return dateIsBefore(b, a) ? 1 : -1;
        } else {
          return dateIsBefore(a, b) ? 1 : -1;
        }
      },
    },
    {
      id: "status",
      label: t("app.company.members.userList.table.columns.status"),
      defaultSortDir: "DESC",
      multipleSort: true,
      sort(a, b, dir) {
        if (dir === "ASC") {
          if (!a.user && b.user) return -1;
          if (a.user && !b.user) return 1;
        }

        if (a.user && !b.user) return -1;
        if (!a.user && b.user) return 1;
      },
    },
    {
      id: "role",
      label: t("app.company.members.userList.table.columns.role"),
      defaultSortDir: "DESC",
      sort(a, b, dir) {
        if (dir === "ASC") return b - a;
        return a - b;
      },
    },
  ],
});

async function addNewMember() {
  if (members.value.find((user) => user.account.personal_information.email === state.mail)) {
    toast.error(t("app.company.members.userList.toasts.emailAlreadyAdded"));
    document.querySelector("[name='add-user-email']").parentNode.parentNode.classList.add("invalid");
    return;
  } else {
    document.querySelector("[name='add-user-email']").parentNode.parentNode.classList.remove("invalid");
  }

  inviteInOrganisation({
    email: state.mail.toLocaleLowerCase(),
    firstName: state.firstName,
    lastName: state.lastName,
    roleId: state.role,
    organisationId: route.params.companyId as string,
  }, {
    onSuccess: () => {
      resetForm();
      toast.success(t("app.company.members.userList.toasts.inviteSuccess"));
    },
    onError: (error) => {
      toast.error(getError(error));
    }
  });
}

function getRole(member: MemberDetails) {
  if (!membersRoles.value || membersRoles.value.pending) return;
  const memberRole = membersRoles.value.data.find((role) => role.memberId === member.member.id);
  return memberRole?.role;
}

async function updateRole(newRole: AppRole, member: MemberDetails) {
  updateMemberRole({
    memberId: member.member.id,
    roleId: newRole.id,
  }, {
    onSuccess: () => {
      toast.success(t("app.company.members.userList.toasts.roleChangeSuccess"));
    },
    onError: (error) => {
      toast.error(t("app.company.members.userList.toasts.roleChangeError"));
    },
  });
}

async function revokeMember(member: MemberDetails) {
  deleteMember({
    organisationId: route.params.companyId as string,
    memberId: member.member.id,
  }, {
    onSuccess: () => {
      toast.success(t("app.company.members.userList.toasts.revoleSuccess"));
    },
    onError: () => {
      toast.error(t("app.company.members.userList.toasts.revokeError"));
    }
  });

  state.showRevokeMember = false;
}

function resetForm() {
  state.mail = "";
  state.role = null;
  document.querySelector("[name='add-user-email']").parentNode.parentNode.classList.remove("invalid");
}
</script>
